import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Cropper from "react-easy-crop";
import Dropdown from "../../Component/Common/Dropdown";
import { InputField } from "../../Component/Common/InputField";
import { userAddValidation } from "../../helper/errorSchema";
import { checkFormError, handleKeyDown, ROLE, getRoleLabel, getAssignableRoles } from "../../helper";
import { getStorage } from "../../helper/storage";
import { getAllAssbApi, getAllBoothApi } from "../../services/voterService";
import { addOrder, singleOrderDetails, updateOrder } from "../../services/orderService";

const SECTIONS = [
  { id: "account", label: "Account Details" },
  { id: "access", label: "Access & Permissions" },
];

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PLACEHOLDER_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const getCroppedImageBlob = async (imageSrc, cropAreaPixels, outputSize = 480) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, cropAreaPixels.x, cropAreaPixels.y, cropAreaPixels.width, cropAreaPixels.height, 0, 0, outputSize, outputSize);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92));
};

const validateProfileImage = (file) => {
  if (!file) return "";
  if (!IMAGE_TYPES.includes(file.type)) return "Only JPG, PNG, or WEBP images are allowed";
  if (file.size > MAX_IMAGE_SIZE) return "Image size must be 2 MB or less";
  return "";
};

// The "Add Users" offcanvas from Orders.js, moved to its own routed page -
// also handles Edit (reached via UserList's edit action as
// /user-management/add?editId=<uuid>), same as AddVoter.js's pattern.
export const AddUser = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");
  const userLogin = getStorage("logged-in");

  const assignableUserTypeOptions = getAssignableRoles(userLogin?.user_type);
  const defaultAssignableUserType =
    assignableUserTypeOptions.find((option) => option.value === ROLE.VOLUNTEER) || assignableUserTypeOptions[0];

  const emptyOrderObj = () => ({
    profile_image: "",
    app_name: "",
    party_name: "",
    username: "",
    mobile_number: "",
    user_type: userLogin?.user_type === ROLE.ADMIN ? defaultAssignableUserType?.value : "",
    assembly_number: "",
    password: "",
    password_confirmation: "",
    view_booth_permissions: "",
    update_booth_permissions: "",
    is_active: true,
  });

  const [isEdit, setIsEdit] = useState(!!editId);
  const [formOrderValue, setFormOrderValue] = useState(emptyOrderObj());
  const [formOrderName, setFormOrderName] = useState({});
  const [formError, setFormError] = useState({});
  const [updateId, setUpdateId] = useState("");
  const [isLoadding, setIsLoadding] = useState(false);

  const [preview, setPreview] = useState(PLACEHOLDER_AVATAR);
  const fileRef = useRef(null);
  const [profileImageError, setProfileImageError] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [boothList, setBoothList] = useState([]);
  const [newBoothLIst, setNewBoothLIst] = useState([]);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const avfBodyRef = useRef(null);
  const avfSectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const avfScrollLockRef = useRef(false);
  const avfScrollLockTimeoutRef = useRef(null);

  useEffect(() => () => window.clearTimeout(avfScrollLockTimeoutRef.current), []);

  const registerSectionRef = (id) => (el) => {
    avfSectionRefs.current[id] = el;
  };

  const scrollToSection = (id) => {
    const container = avfBodyRef.current;
    const target = avfSectionRefs.current[id];
    if (!container || !target) return;
    avfScrollLockRef.current = true;
    window.clearTimeout(avfScrollLockTimeoutRef.current);
    avfScrollLockTimeoutRef.current = window.setTimeout(() => {
      avfScrollLockRef.current = false;
    }, 600);
    container.scrollTo({ top: target.offsetTop - 8, behavior: "smooth" });
    setActiveSection(id);
  };

  const handleAvfBodyScroll = () => {
    if (avfScrollLockRef.current) return;
    const container = avfBodyRef.current;
    if (!container) return;
    const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
    if (atBottom) {
      setActiveSection(SECTIONS[SECTIONS.length - 1].id);
      return;
    }
    const scrollPos = container.scrollTop + 48;
    let current = SECTIONS[0].id;
    SECTIONS.forEach((section) => {
      const target = avfSectionRefs.current[section.id];
      if (target && target.offsetTop <= scrollPos) current = section.id;
    });
    setActiveSection(current);
  };

  const goBackToList = () => navigate("/user-management");

  useEffect(() => {
    if (preview?.startsWith("blob:")) return () => URL.revokeObjectURL(preview);
  }, [preview]);

  useEffect(() => {
    const getBoothList = async () => {
      const res = await getAllBoothApi({ assembly_numbers: formOrderValue.assembly_number });
      const rawBoothNumbers = res?.results?.booth_numbers;
      setBoothList(Array.isArray(rawBoothNumbers) ? rawBoothNumbers.map((item) => ({ label: item, value: item })) : []);
    };
    if (formOrderValue.assembly_number) getBoothList();
  }, [formOrderValue.assembly_number]);

  useEffect(() => {
    setNewBoothLIst([
      { label: "All", value: "All" },
      { label: "None", value: "none" },
      ...(Array.isArray(boothList) ? boothList : []),
    ]);
  }, [boothList]);

  const getOrderDetails = useMutation(singleOrderDetails, {
    onSuccess: (res) => {
      if (!res?.success) return;
      const details = {
        app_name: res?.results?.app_name,
        party_name: res?.results?.party_name,
        username: res?.results.username,
        mobile_number: res?.results.mobile_number,
        assembly_number: res?.results.assembly_number,
        user_type: res?.results.user_type,
        is_active: res?.results.is_active,
        update_booth_permissions: res?.results?.update_booth_permissions,
        view_booth_permissions: res?.results?.view_booth_permissions,
      };
      setFormOrderValue(details);
      const common = (_name, id) => ({ label: getRoleLabel(id), value: Number(id) });
      setFormOrderName({
        update_booth_permissions: res?.results?.update_booth_permissions?.map((item) => ({ label: item, value: item })),
        view_booth_permissions: res?.results?.view_booth_permissions?.map((item) => ({ label: item, value: item })),
        user_type: common("view", res?.results?.user_type),
      });
      setPreview(res?.results?.profile_image || res?.results?.Profile_image || PLACEHOLDER_AVATAR);
      setIsEdit(true);
      setFormError({});
      setUpdateId(res?.results?.uuid);
    },
  });

  useEffect(() => {
    if (editId) getOrderDetails.mutate(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const handleOrderChange = async (name, value) => {
    const Obj = { ...formOrderValue, [name]: value };
    setFormOrderValue(Obj);
    if (!!formError) {
      const error = await checkFormError(Obj, userAddValidation);
      setFormError({ ...error });
    }
  };

  const handleThumbClick = () => {
    if (preview && preview !== PLACEHOLDER_AVATAR) setPreviewModalOpen(true);
    else fileRef.current?.click();
  };

  const handleProfileFileSelect = (file) => {
    const error = validateProfileImage(file);
    if (error) {
      setProfileImageError(error);
      return;
    }
    setProfileImageError("");
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_, areaPixels) => setCroppedAreaPixels(areaPixels);

  const handleCropConfirm = async () => {
    if (!rawImageSrc || !croppedAreaPixels) return;
    const blob = await getCroppedImageBlob(rawImageSrc, croppedAreaPixels);
    const croppedFile = new File([blob], "profile.jpg", { type: "image/jpeg" });
    const nextPreview = URL.createObjectURL(blob);
    setPreview((current) => {
      if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
      return nextPreview;
    });
    handleOrderChange("profile_image", croppedFile);
    setFormOrderValue((current) => ({ ...current, remove_profile_image: false }));
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const removeProfileImage = () => {
    setPreview((current) => {
      if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
      return PLACEHOLDER_AVATAR;
    });
    handleOrderChange("profile_image", "");
    setFormOrderValue((current) => ({ ...current, remove_profile_image: true }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const onSuccessResponse = (message) => {
    toast.success(message || "success");
    queryClient.refetchQueries("getAllOrder");
    goBackToList();
  };

  const addMutation = useMutation(addOrder, {
    onSuccess: (res) => {
      setIsLoadding(false);
      if (res?.success) onSuccessResponse(res?.message);
    },
  });

  const updateMutation = useMutation(updateOrder, {
    onSuccess: (res) => {
      setIsLoadding(false);
      if (res?.success) onSuccessResponse(res?.message);
    },
  });

  const onOrderSubmit = async () => {
    const validationResult = await checkFormError({ ...formOrderValue }, userAddValidation);
    if (validationResult !== true) {
      setIsLoadding(false);
      setFormError(validationResult);
      return;
    }

    setIsLoadding(true);
    try {
      const formData = new FormData();
      formData.append("app_name", userLogin?.app_name);
      formData.append("party_name", userLogin?.party_name);
      formData.append("username", formOrderValue?.username || "");
      formData.append("mobile_number", formOrderValue?.mobile_number || "");
      formData.append("user_type", Number(formOrderValue?.user_type));
      formData.append("assembly_number", formOrderValue?.assembly_number || "");
      formData.append("password", formOrderValue?.password || "");
      formData.append("password_confirmation", formOrderValue?.password_confirmation || "");
      formData.append("is_active", String(formOrderValue?.is_active));

      if (Array.isArray(formOrderValue?.update_booth_permissions)) {
        formOrderValue.update_booth_permissions.forEach((item) => formData.append("update_booth_permissions", item?.value ?? item));
      }
      if (Array.isArray(formOrderValue?.view_booth_permissions)) {
        formOrderValue.view_booth_permissions.forEach((item) => formData.append("view_booth_permissions", item?.value ?? item));
      }
      if (formOrderValue?.profile_image instanceof File) formData.append("profile_image", formOrderValue.profile_image);
      if (formOrderValue?.remove_profile_image) formData.append("remove_profile_image", "true");

      if (isEdit) {
        updateMutation.mutate({ formdata: formData, id: updateId });
      } else {
        addMutation.mutate(formData);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setIsLoadding(false);
    }
  };

  const requiredFieldNames = ["username", "password", "password_confirmation"];
  const requiredErrorCount = requiredFieldNames.filter((name) => !!formError?.[name]).length;

  return (
    <>
      <Modal isOpen toggle={goBackToList} centered className="avf-dialog" contentClassName="avf-content">
        <div className="avf-header">
          <div className="avf-header-top">
            <div>
              <h2 className="avf-title">{isEdit ? "Edit User" : "Add Users"}</h2>
              <p className="avf-subtitle">Fill in the user's details across the sections below</p>
            </div>
            <button type="button" className="avf-close-btn" onClick={goBackToList} aria-label="Close">
              ×
            </button>
          </div>
          <div className="avf-rail">
            {SECTIONS.map((section, index) => (
              <button
                key={section.id}
                type="button"
                className={`avf-step ${activeSection === section.id ? "is-active" : ""}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="avf-step-num">{index + 1}</span>
                <span className="avf-step-label">{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="avf-body" ref={avfBodyRef} onScroll={handleAvfBodyScroll}>
          <div className="avf-photo-card">
            <button
              type="button"
              className="avf-photo-avatar"
              onClick={handleThumbClick}
              title={preview && preview !== PLACEHOLDER_AVATAR ? "View full photo" : "Upload photo"}
            >
              <img
                src={preview || PLACEHOLDER_AVATAR}
                alt="Profile preview"
                onError={({ currentTarget }) => {
                  currentTarget.src = PLACEHOLDER_AVATAR;
                }}
              />
            </button>
            <div className="avf-photo-info">
              <div className="avf-photo-title">Profile photo</div>
              <div className="avf-photo-subtitle">JPG or PNG, square crop recommended, up to 2MB</div>
              {(profileImageError || formError?.Profile_image) && (
                <div className="voter-photo-error">{profileImageError || formError.Profile_image}</div>
              )}
            </div>
            <div className="avf-photo-actions">
              {preview && preview !== PLACEHOLDER_AVATAR && (
                <button type="button" className="avf-photo-remove-btn" onClick={removeProfileImage}>
                  Remove
                </button>
              )}
              <button type="button" className="avf-photo-upload-btn" onClick={() => fileRef.current?.click()}>
                {preview && preview !== PLACEHOLDER_AVATAR ? "Change photo" : "Upload photo"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                hidden
                onChange={({ target: { files } }) => handleProfileFileSelect(files?.[0])}
              />
            </div>
          </div>

          <div className="avf-section" ref={registerSectionRef("account")}>
            <div className="avf-section-head">
              <span className="avf-section-num">1</span>
              <h3 className="avf-section-title">Account Details</h3>
            </div>
            <div className="avf-grid">
              <div className="avf-field">
                <InputField
                  floating
                  className="modal_input orderModal_Input"
                  label="Username"
                  labelClass="FormLableKaran"
                  placeholder="Enter Username"
                  name="username"
                  type="text"
                  labelSpan="*"
                  focus={!!formError?.username}
                  error={formError.username}
                  value={formOrderValue?.username}
                  onChange={({ target: { name, value } }) => handleOrderChange(name, value)}
                />
              </div>
              <div className="avf-field">
                <div className="llt_inputfield">
                  <InputField
                    floating
                    className="modal_input orderModal_Input"
                    label="Mobile Number"
                    labelClass="FormLableKaran"
                    placeholder="Enter Mobile Number"
                    name="mobile_number"
                    type="text"
                    maxLength={10}
                    onKeyDown={handleKeyDown}
                    focus={!!formError?.mobile_number}
                    error={formError.mobile_number}
                    value={formOrderValue?.mobile_number}
                    onChange={({ target: { name, value } }) => handleOrderChange(name, value)}
                  />
                  <span className="llt_inputSpan">+91</span>
                </div>
              </div>
              <div className="avf-field">
                <InputField
                  floating
                  className="modal_input orderModal_Input"
                  label="Set Password"
                  labelClass="FormLableKaran"
                  placeholder="Enter Password"
                  name="password"
                  type="password"
                  labelSpan="*"
                  data="password"
                  showPassword={showPassword1}
                  showHandle={() => setShowPassword1((prev) => !prev)}
                  focus={!!formError?.password}
                  error={formError.password}
                  value={formOrderValue?.password}
                  onChange={({ target: { name, value } }) => handleOrderChange(name, value)}
                />
              </div>
              <div className="avf-field">
                <InputField
                  floating
                  className="modal_input orderModal_Input"
                  label="Repeat Password"
                  labelClass="FormLableKaran"
                  placeholder="Repeat Password"
                  name="password_confirmation"
                  type="password"
                  labelSpan="*"
                  data="password"
                  showPassword={showPassword2}
                  showHandle={() => setShowPassword2((prev) => !prev)}
                  focus={!!formError?.password_confirmation}
                  error={formError.password_confirmation}
                  value={formOrderValue?.password_confirmation}
                  onChange={({ target: { name, value } }) => handleOrderChange(name, value)}
                />
              </div>
            </div>
          </div>

          <div className="avf-section" ref={registerSectionRef("access")}>
            <div className="avf-section-head">
              <span className="avf-section-num">2</span>
              <h3 className="avf-section-title">Access &amp; Permissions</h3>
            </div>
            <div className="avf-grid">
              <div className="avf-field">
                <Dropdown
                  floating
                  label="User Type"
                  className="dropdownoforder"
                  placeholder="Select User Type"
                  name="user_type"
                  options={assignableUserTypeOptions}
                  valueText="value"
                  labelText="label"
                  focus={!!formError?.user_type}
                  error={formError?.user_type}
                  disabled={userLogin?.user_type === ROLE.VIEWER}
                  selectedOption={formOrderName?.user_type}
                  setSelectedOption={(data) => {
                    setFormOrderName({ ...formOrderName, user_type: data });
                    handleOrderChange("user_type", data?.value);
                  }}
                />
              </div>
              <div className="avf-field">
                <InputField
                  floating
                  className="modal_input orderModal_Input"
                  label="Assembly Number"
                  labelClass="FormLableKaran"
                  placeholder="Enter Assembly Number"
                  name="assembly_number"
                  type="text"
                  maxLength={4}
                  onKeyDown={handleKeyDown}
                  focus={!!formError?.assembly_number}
                  error={formError.assembly_number}
                  value={formOrderValue?.assembly_number}
                  onChange={({ target: { name, value } }) => handleOrderChange(name, value)}
                />
              </div>
              <div className="avf-field">
                <Dropdown
                  floating
                  label="View Booth Permissions"
                  className="dropdownoforder"
                  placeholder="Select Booth"
                  name="view_booth_permissions"
                  options={newBoothLIst}
                  valueText="value"
                  labelText="label"
                  isMulti
                  focus={!!formError?.view_booth_permissions}
                  error={formError?.view_booth_permissions}
                  selectedOption={formOrderName?.view_booth_permissions}
                  disabled={!formOrderValue?.user_type || (formOrderValue?.user_type !== ROLE.VIEWER && !formOrderValue?.assembly_number)}
                  setSelectedOption={(data) => {
                    setFormOrderName({ ...formOrderName, view_booth_permissions: data });
                    handleOrderChange("view_booth_permissions", data);
                  }}
                />
              </div>
              <div className="avf-field">
                <Dropdown
                  floating
                  label="Update Booth Permissions"
                  className="dropdownoforder"
                  placeholder="Select Booth"
                  name="update_booth_permissions"
                  options={newBoothLIst}
                  valueText="value"
                  labelText="label"
                  isMulti
                  disabled={!formOrderValue?.user_type || (formOrderValue?.user_type !== ROLE.VIEWER && !formOrderValue?.assembly_number)}
                  focus={!!formError?.update_booth_permissions}
                  error={formError?.update_booth_permissions}
                  selectedOption={formOrderName?.update_booth_permissions}
                  setSelectedOption={(data) => {
                    setFormOrderName({ ...formOrderName, update_booth_permissions: data });
                    handleOrderChange("update_booth_permissions", data);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="avf-footer">
          <div className={`avf-footer-note ${requiredErrorCount > 0 ? "has-errors" : ""}`}>
            {requiredErrorCount > 0
              ? `${requiredErrorCount} of ${requiredFieldNames.length} required fields need attention`
              : `${requiredFieldNames.length} required fields`}
          </div>
          <div className="avf-footer-actions">
            <button type="button" className="avf-btn avf-btn-secondary" onClick={goBackToList}>
              Cancel
            </button>
            {isLoadding ? (
              <button type="button" className="avf-btn avf-btn-primary" disabled>
                Processing...
              </button>
            ) : (
              <button type="button" className="avf-btn avf-btn-primary" onClick={onOrderSubmit}>
                {isEdit ? "Update User" : "Create User"}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <Modal size="md" isOpen={cropModalOpen} toggle={() => setCropModalOpen(false)} centered>
        <ModalHeader toggle={() => setCropModalOpen(false)}>Crop Profile Photo</ModalHeader>
        <ModalBody>
          <div style={{ position: "relative", width: "100%", height: "300px" }}>
            {rawImageSrc && (
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
            <span>Zoom</span>
            <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} style={{ flex: 1 }} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCropModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleCropConfirm}>
            Save
          </Button>
        </ModalFooter>
      </Modal>

      <Modal size="sm" isOpen={previewModalOpen} toggle={() => setPreviewModalOpen(false)} centered>
        <ModalHeader toggle={() => setPreviewModalOpen(false)}>Profile Photo</ModalHeader>
        <ModalBody style={{ textAlign: "center" }}>
          <img src={preview || PLACEHOLDER_AVATAR} alt="Profile full preview" style={{ maxWidth: "100%", borderRadius: 8 }} />
        </ModalBody>
      </Modal>
    </>
  );
};
